<template>
  <div class="flex flex-col gap-3">
    <p class="flex items-center justify-center h-full" v-if="isLoading && levels.length === 0">
      <InlineLoader />
    </p>

    <div v-for="(level, index) in levels" :key="index" class="flex flex-col gap-1">
      <label class="font-semibold">
        {{ level.label }}
        <span v-if="required && index === 0" class="text-red-600">*</span>
      </label>

      <Multiselect
        v-model="level.inputValue"
        @select="handleMySelect($event, index)"
        :options="level.options"
        :canClear="true"
      />

      <p v-show="level.errorMessage || level.meta.valid" class="help-message text-red-500">
        {{ level.errorMessage }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import * as yup from 'yup';
import { ref, reactive, onMounted } from 'vue';
import Multiselect from '@vueform/multiselect';

import InlineLoader from './InlineLoader.vue';
import { getNestedSelectValues } from '@/helpers/nestedSelectCache';

const { t } = useI18n();

const props = defineProps({
  value: { type: String, default: undefined },
  label: { type: String, required: true },
  name: { type: String, required: true },
  required: { type: Boolean, default: true },
  options: { type: Array }, // fallback, если кэш не сработал
  property: { type: Number, required: true },

  defaultParentValue: { type: [String, Number], default: null },
  defaultChildValue: { type: [String, Number], default: null },

  // defaultValues: цепочка выбранных значений (для редактирования)
  defaultValues: { type: Array, default: () => [] },

  dataType: { type: Number, default: undefined },
});

const rules = yup
  .number()
  .min(1, t('warning.REQUIRED_MESSAGE'))
  .required(t('warning.REQUIRED_MESSAGE'));

const levels = ref([]);
const isLoading = ref(false);
const rootRawOptions = ref([]);

const mapToOptions = (items = []) =>
  (items || []).map(item => ({
    value: item.id,
    label: item.name,
    children: item.children || [],
    children_property: item?.children_property || null,
    raw: item,
  }));

const createLevel = (name, options, label, initialValue = null) => {
  const { meta, value, errorMessage, validate, handleChange } = useField(name, rules, {
    initialValue,
  });

  return reactive({
    name,
    label,
    meta,
    inputValue: value,
    errorMessage,
    validate,
    handleChange,
    options,
  });
};

function buildChildrenForSelectedOption(selectedOption, index) {
  levels.value.splice(index + 1);
  if (!selectedOption?.children?.length) return;

  const nextOptions = mapToOptions(selectedOption.children);
  const nextLabel = selectedOption?.children_property?.name || '';

  levels.value.push(createLevel(`${props.name}-${index + 1}`, nextOptions, nextLabel));
}

const handleMySelect = (ev, index) => {
  const selectedOption = levels.value[index]?.options?.find(opt => opt.value === ev);
  buildChildrenForSelectedOption(selectedOption, index);
};

async function initializeLevels() {
  levels.value = [];
  isLoading.value = true;

  try {
    const raw = await getNestedSelectValues(props.property);
    rootRawOptions.value = Array.isArray(raw) ? raw : [];

    // базовый первый уровень
    let firstOptions = mapToOptions(rootRawOptions.value);
    let firstLevel = createLevel(props.name, firstOptions, props.label, props.defaultParentValue);
    levels.value = [firstLevel];

    // восстановление цепочки defaultValues
    if (props.defaultValues && props.defaultValues.length > 0) {
      levels.value = [];

      let currentRaw = rootRawOptions.value;

      for (let i = 0; i < props.defaultValues.length; i++) {
        const currentId = parseInt(props.defaultValues[i]?.id, 10);

        const currentLabel =
          props.defaultValues[i]?.children_property?.name ||
          props.defaultValues[i]?.name ||
          props.label;

        const currentOptions = mapToOptions(currentRaw);

        levels.value.push(
          createLevel(`${props.name}-${i}`, currentOptions, currentLabel, currentId),
        );

        const chosen = currentRaw.find(x => x.id === currentId);
        currentRaw = chosen?.children || [];
      }
    }
  } catch (e) {
    console.error('NestedSelectField: failed to init from cache', e);

    // fallback если кэш умер
    const fallback = mapToOptions(props.options || []);
    levels.value = [createLevel(props.name, fallback, props.label, props.defaultParentValue)];
  } finally {
    isLoading.value = false;
  }
}

const validateField = async () => {
  if (!levels.value.length) {
    return { value: [], valid: false, mode: 'multi', property: props.property };
  }

  const results = await Promise.all(levels.value.map(level => level.validate()));
  const valid = results.every(r => r.valid);

  const values = levels.value
    .map(level => level.inputValue)
    .filter(v => v !== null && v !== undefined && v !== '')
    .map(v => parseInt(v, 10))
    .filter(v => !isNaN(v));

  return { value: values, valid, mode: 'multi', property: props.property };
};

defineExpose({ validateField });

onMounted(() => {
  initializeLevels();
});
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
